import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import Button from '~/components/Button';
import Gap from '~/components/Gap';
import Header from '~/components/Header';
import MainContainer from '~/components/MainContainer';
import Text from '~/components/Text';
import ListTickIcon from '~/icons/ListTickIcon';
import { setPaymentCompleted, setPaymentInitiated, setPendingPlan } from '~/store/slices/user';
import { RootState } from '~/types';
import { PlanType } from '~/utils/payment';
import BillingHistorySection from './BillingHistorySection';
import BillingBanner from '~/components/BillingBanner';
import * as Styles from './styles';
import { analytics, ANALYTICS_EVENTS } from '~/services/analyticsService';
import { IPlan } from '~/store/slices/plans';

const formatPrice = (price: number | string | undefined) => {
  if (price == null) return '--';
  const num = typeof price === 'string' ? parseFloat(price) : price;
  return isNaN(num) ? '--' : num % 1 === 0 ? num.toFixed(0) : num.toString();
};

const Billing = () => {
  const dispatch = useDispatch();
  const { currentPlan } = useSelector((state: RootState) => state.user);
  const { plans } = useSelector((state: RootState) => state.plans);
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref');

  // Get plan prices from Redux
  const freePlan = plans.find((p: IPlan) => p.name === 'free');
  const proPlan = plans.find((p: IPlan) => p.name === 'pro');
  const infinityPlan = plans.find((p: IPlan) => p.name === 'infinity');

  const handlePlanUpgrade = (planType: PlanType) => {
    analytics.capture(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      screen_name: 'Plans',
      button_name: 'Choose Plan',
      button_id: planType === 'pro' ? 'pro_plan_cta' : 'infinity_plan_cta',
      ref: ref || undefined,
    });

    dispatch(setPendingPlan(planType));
    dispatch(setPaymentInitiated(true));
    dispatch(setPaymentCompleted(false));
  };

  return (
    <div>
      <Header headerText="Plans & Billing" />

      <MainContainer>
        {currentPlan === 'free' && ref && (
          <BillingBanner
            variant="warning"
            title="Limit Reached. Time to Scale. 🚀"
            description={`You've reached your usage limit on the Free Plan. Select a paid plan below to create more ${ref}, and access premium features.`}
          />
        )}

        <Styles.HeaderContent className="full-width">
          <div>
            <Text size="smd" className="bold">
              Plans
            </Text>
          </div>
        </Styles.HeaderContent>

        <Gap t={24} />

        {/* Plans Section */}
        <Styles.PricingSection>
          <Styles.PricingCards>
            {/* Starter Plan */}
            <Styles.Card isCenter={!currentPlan || currentPlan === null || currentPlan === 'free'}>
              <Styles.CardTitle>Free</Styles.CardTitle>
              <Styles.CardDescription>Perfect for getting started</Styles.CardDescription>
              <Styles.CardPriceContainer>
                <Styles.CardPrice>${formatPrice(freePlan?.price)}</Styles.CardPrice>
                <Styles.CardPriceDescription>/month</Styles.CardPriceDescription>
              </Styles.CardPriceContainer>
              <Styles.CardDescription>Includes</Styles.CardDescription>
              <Styles.CardFeatures>
                <li>
                  <ListTickIcon />
                  {freePlan?.invoice_limit === -1 ? 'Unlimited' : freePlan?.invoice_limit}{' '}
                  {freePlan?.invoice_limit === 1 ? 'Invoice' : 'Invoices'} Total
                </li>
                <li>
                  <ListTickIcon />
                  {freePlan?.client_limit === -1 ? 'Unlimited' : freePlan?.client_limit}{' '}
                  {freePlan?.client_limit === 1 ? 'Client' : 'Clients'} Maximum
                </li>
                <li>
                  <ListTickIcon />
                  {freePlan?.business_limit === -1 ? 'Unlimited' : freePlan?.business_limit}{' '}
                  Business {freePlan?.business_limit === 1 ? 'Profile' : 'Profiles'}
                </li>
                <li>
                  <ListTickIcon />
                  Use your own branding on Invoices
                </li>
                <li>
                  <ListTickIcon />
                  Limited access to new features
                </li>
              </Styles.CardFeatures>
              {(!currentPlan || currentPlan === null || currentPlan === 'free') && (
                <Button variant="secondary" size="full" disabled>
                  Current Plan
                </Button>
              )}
            </Styles.Card>

            {/* Pro Plan */}
            <Styles.Card isCenter={currentPlan === 'pro'}>
              <Styles.CardTitle>Pro</Styles.CardTitle>
              <Styles.CardDescription>For small and growing businesses</Styles.CardDescription>
              <Styles.CardPriceContainer>
                <Styles.CardPrice>${formatPrice(proPlan?.price)}</Styles.CardPrice>
                <Styles.CardPriceDescription>/month</Styles.CardPriceDescription>
              </Styles.CardPriceContainer>
              <Styles.CardDescription>Includes</Styles.CardDescription>
              <Styles.CardFeatures>
                <li>
                  <ListTickIcon />
                  <p>
                    <span className="f-med">Unlimited</span> Invoices
                  </p>
                </li>
                <li>
                  <ListTickIcon />
                  <p>
                    <span className="f-med">Unlimited</span> Clients
                  </p>
                </li>
                <li>
                  <ListTickIcon />
                  <p>
                    <span className="f-med">Unlimited</span> business profiles
                  </p>
                </li>
                <li>
                  <ListTickIcon />
                  <p>Recurring Invoices</p>
                </li>
                <li>
                  <ListTickIcon />
                  <p>Use your own branding on Invoices</p>
                </li>
                <li>
                  <ListTickIcon />
                  <p>Priority access to new features</p>
                </li>
                <li>
                  <ListTickIcon />
                  <p>Priority email support</p>
                </li>
              </Styles.CardFeatures>

              {/* Show button only if: current plan is pro OR user can upgrade from free */}
              {(currentPlan === 'pro' ||
                !currentPlan ||
                currentPlan === null ||
                currentPlan === 'free') && (
                <Button
                  variant={currentPlan === 'pro' ? 'secondary' : 'primary'}
                  size="full"
                  onClick={() => handlePlanUpgrade('pro')}
                  disabled={currentPlan === 'pro'}
                >
                  {currentPlan === 'pro' ? 'Current Plan' : 'Choose Plan'}
                </Button>
              )}
            </Styles.Card>

            {/* Infinity Plan */}
            <Styles.Card isCenter={currentPlan === 'infinity'}>
              <Styles.CardTag>LIMITED SLOTS LEFT</Styles.CardTag>
              <Styles.CardTitle>Infinity</Styles.CardTitle>
              <Styles.CardDescription>Lifetime access to everything</Styles.CardDescription>
              <Styles.CardPriceContainer>
                <Styles.CardPrice>${formatPrice(infinityPlan?.price)}</Styles.CardPrice>
                <Styles.CardPriceDescription>One-Time Payment</Styles.CardPriceDescription>
              </Styles.CardPriceContainer>
              <Styles.CardDescription>Includes</Styles.CardDescription>
              <Styles.CardFeatures>
                <li>
                  <ListTickIcon />
                  <p>Lifetime access</p>
                </li>
                <li>
                  <ListTickIcon />
                  <span>
                    Everything in our <span className="f-med">Pro Plan</span>, plus access to all{' '}
                    <span className="f-med">future Pro features</span>
                  </span>
                </li>
              </Styles.CardFeatures>
              {/* Show button only if: current plan is infinity OR user can upgrade from free/pro */}
              {(currentPlan === 'infinity' ||
                !currentPlan ||
                currentPlan === null ||
                currentPlan === 'free' ||
                currentPlan === 'pro') && (
                <Button
                  variant={currentPlan === 'infinity' ? 'secondary' : 'primary'}
                  size="full"
                  onClick={() => handlePlanUpgrade('infinity')}
                  disabled={currentPlan === 'infinity'}
                >
                  {currentPlan === 'infinity' ? 'Current Plan' : 'Choose Plan'}
                </Button>
              )}
            </Styles.Card>
          </Styles.PricingCards>
        </Styles.PricingSection>

        {/* Billing History Section */}
        <BillingHistorySection />
      </MainContainer>

      <Gap t={32} />
    </div>
  );
};

export default Billing;
